import moment from 'moment-timezone';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuditFilterAction, AuditFilterUser, getFilterOptions} from 'audit/AuditDAO';
import notifications from 'notifications';
import {MOMENT_DATE_FORMAT} from 'util/DateConstants';

import {FILTER_MODE, isDateRangeValid} from './filters/FlexibleDateRangePicker';
import type {DateRange, FilterMode} from './filters/FlexibleDateRangePicker';

// Keep in sync with benchling/exports/format_exported_datetime.py
const EXPORTED_DATE_FORMAT_TO_MOMENT: Record<string, string> = {
  DDMONYYYY: 'DDMMMYYYY',
  YYYYMMDD: MOMENT_DATE_FORMAT,
  MMDDYYYY: 'MM-DD-YYYY',
  DDMMYYYY: 'DD-MM-YYYY',
};

interface UseAuditLogFiltersProps {
  ids: Array<string>;
  enabled: boolean;
  resultsWarehouseIds?: Array<string>;
  relatedResultsModelId?: string;
}

interface CommittedDateFilter {
  startDateISO?: string;
  endDateISO?: string;
}

interface UseAuditLogFiltersResult {
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  dateMode: FilterMode;
  setDateMode: (mode: FilterMode) => void;
  committedDateFilter: CommittedDateFilter;
  selectedUserIds: Array<string>;
  setSelectedUserIds: (ids: Array<string>) => void;
  selectedActionValues: Array<string>;
  setSelectedActionValues: (values: Array<string>) => void;
  selectedReasonCodes: Array<string>;
  setSelectedReasonCodes: (values: Array<string>) => void;
  filterUsers: Array<AuditFilterUser>;
  filterActions: Array<AuditFilterAction>;
  filterReasonCodes: Array<string> | null;
  filterOptionsError: boolean;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  displayDateFormat: string | undefined;
}

export default function useAuditLogFilters({
  ids,
  enabled,
  resultsWarehouseIds,
  relatedResultsModelId,
}: UseAuditLogFiltersProps): UseAuditLogFiltersResult {
  const [dateRange, setDateRange] = useState<DateRange>({startDate: null, endDate: null});
  const [dateMode, setDateMode] = useState<FilterMode>(FILTER_MODE.BETWEEN);
  const [committedDateFilter, setCommittedDateFilter] = useState<CommittedDateFilter>({});
  const [selectedUserIds, setSelectedUserIds] = useState<Array<string>>([]);
  const [selectedActionValues, setSelectedActionValues] = useState<Array<string>>([]);
  const [selectedReasonCodes, setSelectedReasonCodes] = useState<Array<string>>([]);

  const [filterUsers, setFilterUsers] = useState<Array<AuditFilterUser>>([]);
  const [filterActions, setFilterActions] = useState<Array<AuditFilterAction>>([]);
  const [filterReasonCodes, setFilterReasonCodes] = useState<Array<string> | null>(null);
  const [filterOptionsError, setFilterOptionsError] = useState(false);
  const [displayTimezone, setDisplayTimezone] = useState('UTC');
  const [displayDateFormat, setDisplayDateFormat] = useState<string>();

  const loadFilterOptions = useCallback(async () => {
    try {
      setFilterOptionsError(false);
      const response = await getFilterOptions(ids, {
        resultsWarehouseIds,
        relatedResultsModelId,
      });
      setFilterUsers(response.users);
      setFilterActions(response.actions);
      setFilterReasonCodes(response.reasonCodes);
      setDisplayTimezone(response.displayTimezone);
      setDisplayDateFormat(EXPORTED_DATE_FORMAT_TO_MOMENT[response.displayDateFormat]);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (err) {
      setFilterOptionsError(true);
      notifications.error('Failed to load filter options. Filters have been disabled.');
    }
  }, [ids, resultsWarehouseIds, relatedResultsModelId]);

  useEffect(() => {
    if (enabled) {
      loadFilterOptions();
    }
  }, [enabled, loadFilterOptions]);

  // Only update the committed date filter when the selection is complete. In "between" mode
  // this means both dates are set (or both cleared). This prevents removing the active filter
  // while the user is mid-selection picking a new range
  useEffect(() => {
    const hasOnlyOneDate = (dateRange.startDate == null) !== (dateRange.endDate == null);
    const isBetweenMidSelection = dateMode === 'between' && hasOnlyOneDate;
    if (isBetweenMidSelection || !isDateRangeValid(dateRange, dateMode)) {
      return;
    }
    setCommittedDateFilter({
      startDateISO: dateRange.startDate
        ? moment.tz(dateRange.startDate, 'YYYY-MM-DD', displayTimezone).startOf('day').format()
        : undefined,
      endDateISO: dateRange.endDate
        ? moment.tz(dateRange.endDate, 'YYYY-MM-DD', displayTimezone).endOf('day').format()
        : undefined,
    });
  }, [dateRange, dateMode, displayTimezone]);

  const hasActiveFilters = useMemo(
    () =>
      dateRange.startDate != null ||
      dateRange.endDate != null ||
      selectedUserIds.length > 0 ||
      selectedActionValues.length > 0 ||
      selectedReasonCodes.length > 0,
    [dateRange, selectedUserIds, selectedActionValues, selectedReasonCodes]
  );

  const clearAllFilters = useCallback(() => {
    setDateRange({startDate: null, endDate: null});
    setDateMode(FILTER_MODE.BETWEEN);
    setSelectedUserIds([]);
    setSelectedActionValues([]);
    setSelectedReasonCodes([]);
  }, []);

  return {
    dateRange,
    setDateRange,
    dateMode,
    setDateMode,
    committedDateFilter,
    selectedUserIds,
    setSelectedUserIds,
    selectedActionValues,
    setSelectedActionValues,
    selectedReasonCodes,
    setSelectedReasonCodes,
    filterUsers,
    filterActions,
    filterReasonCodes,
    filterOptionsError,
    hasActiveFilters,
    clearAllFilters,
    displayDateFormat,
  };
}
